//import http from '@services/http';
import type { AxiosInstance } from 'axios';
import type { Transcripts } from './interfaces/index.js';
import { transformConferenceTagSummaryItem, transformConferenceTagSummaryFeedback, transformContentSummary, transformTagSummary } from './transformers/conferences.tag.summary.js';
import qs from './utils/query-string.js';

export class TranscriptsSummaryClient {
  constructor(private authenticatedClient: AxiosInstance) { }

  downloadSummary = (data: Transcripts.DownloadSummary.Request) => {
    const params = {
      format: data.format || `pptx`,
      tagIds: data.tagIds,
    };

    return this.authenticatedClient.get<Blob>(`/transcripts/${data.transcriptId}/summary/export?${qs.stringify(params, { arrayFormat: 'bracket' })}`, { responseType: 'blob' })
      .then<Transcripts.DownloadSummary.Response>(res => ({
        file: res.data,
        name: `Call Transcript Summary.${params.format}`,
      }));
  };

  fetchSummary = (data: Transcripts.FetchSummary.Request): Promise<Transcripts.FetchSummary.Response> => {
    return this.authenticatedClient.get<Transcripts.FetchSummary.Response<string>>(`/transcripts/${data.transcriptId}/summary`)
      .then(res => {
        return {
          content: transformContentSummary(res.data.content)!,
          items: res.data.items.map(transformConferenceTagSummaryItem),
        };
      });
  };

  getTagSummaryFeedbackSummary = ({ transcriptId, tagSummaryId }: Transcripts.GetTagSummaryFeedbackSummary.Request): Promise<Transcripts.GetTagSummaryFeedbackSummary.Response> => {
    return this.authenticatedClient.get<Transcripts.GetTagSummaryFeedbackSummary.Response<string>>(`/transcripts/${transcriptId}/summary/${tagSummaryId}/feedback-summary`)
      .then(res => {
        return {
          ...res.data,
          items: res.data.items.map(transformConferenceTagSummaryFeedback),
        };
      });
  };

  getTagSummaryFeedback = ({ transcriptId, tagSummaryId }: Transcripts.GetTagSummaryFeedbackSummary.Request) => {
    return this.authenticatedClient.get<Transcripts.GetTagSummaryFeedback.Response>(`/transcripts/${transcriptId}/summary/${tagSummaryId}/feedback`).then(x => x.data);
  };

  updateTagSummaryRating = ({ transcriptId, tagSummaryId, ...data }: Transcripts.UpdateTagSummaryRating.Request): Promise<Transcripts.UpdateTagSummaryRating.Response> => {
    return this.authenticatedClient.patch(`/transcripts/${transcriptId}/summary/${tagSummaryId}/rating`, data).then(x => x.data);
  };

  updateTagSummaryNotes = ({ transcriptId, tagSummaryId, ...data }: Transcripts.UpdateTagSummaryNotes.Request): Promise<Transcripts.UpdateTagSummaryNotes.Response> => {
    return this.authenticatedClient.patch(`/transcripts/${transcriptId}/summary/${tagSummaryId}/notes`, data).then(x => x.data);
  };

  updateTagSummaryFeedback = ({ transcriptId, tagSummaryId, ...data }: Transcripts.UpdateTagSummaryFeedback.Request): Promise<Transcripts.UpdateTagSummaryFeedback.Response> => {
    return this.authenticatedClient.patch(`/transcripts/${transcriptId}/summary/${tagSummaryId}/feedback`, data).then(x => x.data);
  };

  getTagSummaryNotes = ({ transcriptId, tagSummaryId }: Transcripts.GetTagSummaryNotes.Request): Promise<Transcripts.GetTagSummaryNotes.Response> => {
    return this.authenticatedClient.get(`/transcripts/${transcriptId}/summary/${tagSummaryId}/notes`).then(x => x.data);
  };

  generateTagSummaries = ({ transcriptId, tagIds, process }: Transcripts.GenerateTagSummaries.Request): Promise<Transcripts.GenerateTagSummaries.Response> => {
    return this.authenticatedClient.post(`/transcripts/${transcriptId}/summary/generate`, {
      tagIds,
      process,
    }).then(x => x.data);
  };

  getTagSummaryCitations = ({ transcriptId, tagSummaryId }: Transcripts.GetTagSummaryCitations.Request): Promise<Transcripts.GetTagSummaryCitations.Response> => {
    return this.authenticatedClient.get(`/transcripts/${transcriptId}/summary/${tagSummaryId}/citations`).then(x => x.data);
  };

  getTagSummaryVersions = ({ transcriptId, tagSummaryId }: Transcripts.GetTagSummaryVersions.Request): Promise<Transcripts.GetTagSummaryVersions.Response> => {
    return this.authenticatedClient.get<Transcripts.GetTagSummaryVersions.Response<string>>(`/transcripts/${transcriptId}/summary/${tagSummaryId}/versions`)
      .then(x => x.data)
      .then(res => {
        return {
          ...res,
          items: res.items.map(item => transformTagSummary(item)!),
        };
      });
  };
}

export default TranscriptsSummaryClient;